import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import { productAutopilotPageEndpoint, updateProductPdpUrl } from "../../api/endpoints";
import { apiErrorCode } from "../../utils/apiError";
import { intervalLabel, scheduleLabel } from "../../utils/plans";
import { AnalysisScheduleFields } from "../Analysis/AnalysisScheduleFields";

// A page's PDP Autopilot settings, opened from its gold "Autopilot on" chip
// (Product Pages, Video settings > Product pages, the Autopilot tab): what
// Autopilot does, how often it re-analyzes the page (the cadence belongs to
// Autopilot — off it, a page is analyzed on demand), and turning it off.

export interface PdpAutopilotPage {
    id: number;
    pdp_url: string;
    score_interval?: string | null;
    // The day it runs: weekday for weekly / every 2 weeks, date for monthly.
    score_weekday?: number | null;
    score_day_of_month?: number | null;
    // The fastest cadence the page's plan allows; faster ones are disabled.
    max_score_interval?: string | null;
}

export function PdpAutopilotDialog({
    open,
    onClose,
    desireid,
    brandId = null,
    page,
    onChanged,
}: {
    open: boolean;
    onClose: () => void;
    desireid: number;
    brandId?: number | null;
    page: PdpAutopilotPage;
    onChanged: () => Promise<unknown> | void;
}) {
    const initial = () => ({
        interval: page.score_interval || "",
        weekday: page.score_weekday === undefined ? null : page.score_weekday,
        dayOfMonth: page.score_day_of_month === undefined ? null : page.score_day_of_month,
    });
    const [pick, setPick] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [turningOff, setTurningOff] = useState(false);
    useEffect(() => {
        if (open) setPick(initial());
    }, [open, page.score_interval, page.score_weekday, page.score_day_of_month]);

    const busy = saving || turningOff;

    const save = async () => {
        const same = pick.interval === (page.score_interval || "")
            && pick.weekday === (page.score_weekday === undefined ? null : page.score_weekday)
            && pick.dayOfMonth === (page.score_day_of_month === undefined ? null : page.score_day_of_month);
        if (!pick.interval || same) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            await axiosInstant.patch(updateProductPdpUrl(desireid, page.id), {
                score_interval: pick.interval,
                ...(pick.weekday !== null ? { score_weekday: pick.weekday } : {}),
                ...(pick.dayOfMonth !== null ? { score_day_of_month: pick.dayOfMonth } : {}),
            });
            toast.success(`Autopilot analyzes this page ${scheduleLabel(pick.interval, pick.weekday, pick.dayOfMonth).toLowerCase()}.`);
            await onChanged();
            onClose();
        } catch (e: any) {
            const max = e?.response?.data?.max;
            if (apiErrorCode(e) === "IntervalExceedsPlanMax") {
                toast.error(`That is faster than the brand's plan allows${max ? ` (max: ${intervalLabel(max)})` : ""}.`);
            } else {
                toast.error("Couldn't save the analysis interval. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    const turnOff = async () => {
        setTurningOff(true);
        try {
            await axiosInstant.patch(productAutopilotPageEndpoint(desireid, page.id, brandId), { enabled: false });
            toast.success("Page taken off autopilot — it is now analyzed on demand.");
            await onChanged();
            onClose();
        } catch {
            toast.error("Couldn't turn off autopilot for this page. Please try again.");
        } finally {
            setTurningOff(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                PDP Autopilot
                <IconButton aria-label="Close" onClick={onClose} size="small" disabled={busy}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <p className="text-[13px] text-text-2 leading-[1.5] mb-3">
                    Autopilot keeps this product page under watch: it re-analyzes the page on the interval below,
                    baselines its score, recommends changes to improve it, and measures each change once it is live.
                    Off autopilot, the page is only analyzed when you run an analysis.
                </p>
                <p className="text-[12px] text-muted truncate mb-4" title={page.pdp_url}>
                    {page.pdp_url}
                </p>
                <AnalysisScheduleFields
                    label="PDP analysis interval"
                    value={pick}
                    onChange={setPick}
                    maxInterval={page.max_score_interval || null}
                    helperText={
                        page.max_score_interval
                            ? `The brand's plan allows ${intervalLabel(page.max_score_interval).toLowerCase()} at most.`
                            : "How often Autopilot analyzes this page."
                    }
                    disabled={busy}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                <Button color="error" onClick={turnOff} disabled={busy}>
                    {turningOff ? "Turning off…" : "Turn off autopilot"}
                </Button>
                <span>
                    <Button onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button variant="contained" disableElevation onClick={save} disabled={busy}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </span>
            </DialogActions>
        </Dialog>
    );
}
